import { EventEmitter, inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { MessageService } from '@factor_ec/ui';
import { Operation, StorageService } from '@factor_ec/utils';
import { firstValueFrom } from 'rxjs';

import { Credit } from '@/main/models/credit';
import { CreditComponent } from '@/main/components/credit/credit.component';
import { PaymentService } from '@/main/services/payment.service';
import { BeneficiaryComponent } from '@/main/components/beneficiary/beneficiary.component';
import { ApiService } from '@/core/api.service';
import { SessionService } from '@/core/session.service';
import { Group, Simulation } from '../models/simulation';
import { SharingOptionsComponent } from '../components/sharing-options/sharing-options.component';
import { environment } from '@/environments/environment';
import { ProgressService } from '@/shared/progress.service';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { AppService } from '@/core/app.service';
import { SimulationComponent } from '../components/simulation/simulation.component';
import { file } from 'jszip';

/**
 * @description Servicio para gestionar operaciones relacionadas con créditos
 * @class CreditService
 */
@Injectable({
  providedIn: 'root'
})
export class CreditService {
  // Dependency injection
  private readonly appService = inject(AppService);
  private readonly apiService = inject(ApiService);
  private readonly sessionService = inject(SessionService);
  private readonly dialog = inject(MatDialog);
  private readonly messageService = inject(MessageService);
  private readonly paymentService = inject(PaymentService);
  private readonly progressService = inject(ProgressService);
  private readonly storageService = inject(StorageService);

  // Events
  public readonly change: EventEmitter<Operation> = new EventEmitter();

  /**
   * @description Abre el selector de archivos del sistema y devuelve los archivos seleccionados en base64
   * @param {Object} [options] - Opciones del selector
   * @param {string} [options.accept] - Tipos MIME aceptados
   * @param {boolean} [options.multiple] - Si permite múltiples archivos
   * @returns {Promise<any[] | null>} Archivos con propiedad data en base64, o null si se cancela
   */
  public async openFile(options?: {
    accept?: string;
    multiple?: boolean;
  }): Promise<any[] | null> {
    return new Promise((resolve, reject) => {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.style.display = 'none';
      if (options?.accept) {
        fileInput.accept = options.accept;
      }
      fileInput.multiple = options?.multiple ?? false;
      document.body.appendChild(fileInput);
      let changeHandled = false;
      const cleanUp = () => {
        window.removeEventListener('focus', onFocus);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        if (fileInput.parentNode) {
          document.body.removeChild(fileInput);
        }
      };
      const onFocus = () => {
        setTimeout(() => {
          if (!changeHandled) {
            cleanUp();
            resolve(null);
          }
        }, 1500);
      };
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          setTimeout(() => {
            if (!changeHandled) {
              cleanUp();
              resolve(null);
            }
          }, 1500);
        }
      };
      fileInput.addEventListener('change', async (event: any) => {
        changeHandled = true;
        const files = Array.from(event.target.files || []) as File[];
        if (!files.length) {
          cleanUp();
          resolve(null);
          return;
        }
        try {
          const result = await Promise.all(
            files.map(file => {
              return new Promise((res, rej) => {
                const reader = new FileReader();
                reader.onload = () => {
                  res(Object.assign(file, { data: reader.result }));
                };
                reader.onerror = err => {
                  rej(reader.error);
                };
                reader.readAsDataURL(file);
              });
            })
          );
          cleanUp();
          resolve(result);
        } catch (error) {
          cleanUp();
          reject(error);
        }
      });
      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', onVisibilityChange);
      fileInput.click();
    });
  }

  /**
   * @description Adjunta un documento a un crédito o simulación
   * @param {Credit | Simulation} credit - Crédito o simulación al que se adjuntará el documento
   * @returns {Promise<File[]>} Promesa con los archivos adjuntados
   */
  async attachDocument(credit: Credit | Simulation): Promise<File[]> {
    return new Promise(async (resolve, reject) => {
      const lenderId = this.sessionService.settings()?.lenderId;
      try {
        if (credit.documentLoaded) {
          const confirm = await firstValueFrom(
            this.messageService.show(
              $localize`You already have a supporting document uploaded. Do you wish to replace it with another?`,
              {
                type: 'modal',
                class:
                  'text-center d-flex flex-column align-items-center gap-3',
                icon: {
                  name: 'warning',
                  class: 'text-warning ft-icon--4',
                  collection: 'factoricons-slim'
                },
                actions: [
                  {
                    label: $localize`Cancel`,
                    value: 0,
                    type: 'stroked',
                    class: 'flex-grow-1'
                  },
                  {
                    label: $localize`Accept`,
                    value: 1,
                    type: 'flat',
                    class: 'flex-grow-1'
                  }
                ]
              }
            )
          );
          if (confirm === 0) {
            return;
          }
        }
        const attachmentMaxSize =
          this.sessionService
            .settings()
            ?.validations.find(v => v.id.trim() === 'MPTMX')?.value || 0;
        const files: any[] | null = await this.openFile({
          // accept: environment.filesAllowed.map(f => f.mimetype).join(','),
          multiple: false
        });
        if (files) {
          let message = '';
          const notAllowed = files.some(file => {
            if (file.size / (1024 * 1024) > Number(attachmentMaxSize)) {
              message = $localize`The maximum allowed size is ${attachmentMaxSize} MB. Please check the file size and try again.`;
              return true;
            }
            if (
              !environment.filesAllowed.map(f => f.mimetype).includes(file.type)
            ) {
              message = $localize`The file format is not valid. You can only upload ZIP, Word, PDF, or JPG/PNG image files.`;
              return true;
            }
            return false;
          });
          if (notAllowed) {
            this.messageService.show(message, {
              class: 'ft-message--warning',
              icon: 'warning'
            });
            reject();
          } else {
            const uploadPromises = files.map(async file => {
              const body: any = {
                lenderId,
                operationNumber: credit.operationNumber,
                countryId: credit.countryId,
                productTypeId: 'CON',
                base64Document: file.data.split(',')[1],
                fileExtension: environment.filesAllowed.find(
                  f => f.mimetype === file.type
                )?.extension
              };
              this.progressService.show({
                modal: true
              });
              try {
                await this.apiService.call({
                  microServiceName: 'WSMPCreditManagement',
                  resourcePath: `/document/process-attach-document`,
                  method: 'POST',
                  useToken: true,
                  body,
                  bodyEncrypted: true
                });
              } finally {
                this.progressService.hide();
              }
              this.messageService.show(
                $localize`Document attached successfully.`,
                { class: 'ft-message--success', icon: 'check--circle' }
              );
              this.change.emit({ method: 'update', entity: credit });
            });
            await Promise.all(uploadPromises);
            resolve(files); // Se resuelve la promesa solo si todo fue exitoso
          }
        }
      } catch (error) {
        reject(error); // Si algo falla, se rechaza la promesa
      }
    });
  }

  /**
   * @description Cancela un crédito
   * @param {Credit} credit - Crédito a cancelar
   * @returns {Promise<void>} Promesa que resuelve cuando se completa la cancelación
   */
  async cancel(credit: Credit): Promise<void> {
    const value = await firstValueFrom(
      this.messageService.show(
        $localize`Do you really want to void the credit #${credit.operationNumber}?`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'warning',
            class: 'text-warning ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Cancel`,
              value: 0,
              type: 'stroked',
              class: 'flex-grow-1'
            },
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      )
    );
    if (value === 1) {
      const response = await this.apiService.call({
        microServiceName: 'WSMPCreditManagement',
        resourcePath: `/credit/void-credit`,
        method: 'POST',
        useToken: true,
        body: {
          countryId: credit.countryId,
          productTypeId: 'CON',
          operationNumber: credit.operationNumber
        },
        bodyEncrypted: true
      });
      credit.status.id = 'CAN';
      this.change.emit({
        method: 'update',
        entity: credit
      });
      this.messageService.show($localize`Loan successfully canceled.`, {
        class: 'ft-message--success',
        icon: 'check--circle'
      });
    }
    return value;
  }

  /**
   * @description Abre el diálogo de creación de crédito
   */
  create(group?: Group): Promise<Operation> {
    const config = {
      data: {
        group,
        type: 'credit'
      },
      panelClass: ['ft-dialog', 'ft-dialog--stacked'],
      height: '100vh',
      width: '682px',
      position: { left: 'auto', right: '0' }
    };
    return new Promise<Operation>(resolve => {
      const dialogRef = this.dialog.open(SimulationComponent, config);
      dialogRef.componentInstance.afterSubmit.subscribe(
        (operation: Operation) => {
          this.change.emit(operation);
          resolve(operation);
        }
      );
    });
  }

  /**
   * @description Obtiene las acciones contextuales disponibles para un crédito
   * @param {Credit} credit - Crédito objetivo
   * @returns {any[]} Array de acciones disponibles
   */
  getContextActions(credit: Credit, type?: 'detail' | 'list'): any[] {
    let actions: any[];
    if (!credit?.operationNumber && !credit?.operation.number) {
      actions = [];
    } else {
      actions = [
        {
          id: 'payments',
          type: 'group',
          children: [
            {
              id: 'regularPayment',
              label: $localize`Record payment`,
              visible:
                credit.status &&
                ['VIG', 'VCD'].includes(credit.status.id) &&
                this.sessionService.isGranted('btn-mnu-record-payment'),
              click: () => {
                if (credit.amountOverdueToDate > 0) {
                  this.paymentService.open({ type: 'regularPayment', credit });
                } else {
                  this.messageService.show(
                    $localize`You have no pending payments.`,
                    {
                      class: 'ft-message--info',
                      icon: 'information'
                    }
                  );
                }
              }
            },
            {
              id: 'additionalPayment',
              label: $localize`Reduce term/payment`,
              visible:
                credit.status &&
                ['VIG', 'VCD'].includes(credit.status.id) &&
                this.sessionService.isGranted('btn-mnu-reduce-term-payment'),
              click: () => {
                this.paymentService.open({ type: 'additionalPayment', credit });
              }
            },
            {
              id: 'prePayment',
              label: $localize`Pre-pay`,
              visible:
                credit.status &&
                ['VIG', 'VCD'].includes(credit.status.id) &&
                this.sessionService.isGranted('btn-mnu-pre-pay'),
              click: () => {
                this.paymentService.open({ type: 'prePayment', credit });
              }
            },
            {
              id: 'viewPayments',
              label: $localize`View payments`,
              visible:
                type !== 'detail' &&
                this.sessionService.isGranted('btn-mnu-view-payments'),
              click: () => {
                this.open(credit, 'payments');
              }
            }
          ].filter(a => a.visible)
        },
        {
          id: 'documents',
          type: 'group',
          children: [
            {
              id: 'editBeneficiary',
              label: $localize`Edit properties`,
              visible:
                credit.status &&
                ['VIG', 'VCD'].includes(credit.status.id) &&
                this.sessionService.isGranted('btn-mnu-edit-credit'),
              click: () => {
                this.openBeneficiary(credit);
              }
            },
            {
              id: 'generateDocument',
              label: $localize`Generate formal document`,
              visible:
                credit.status &&
                ['VIG', 'VCD'].includes(credit.status.id) &&
                credit.generateFormalDocument &&
                this.sessionService.isGranted(
                  'btn-mnu-generate-formal-document'
                ),
              click: () => {
                this.generateDocument(credit);
              }
            },
            {
              id: 'attachDocument',
              label: $localize`Attach supporting document`,
              visible:
                credit.status &&
                ['VIG', 'VCD'].includes(credit.status.id) &&
                this.sessionService.isGranted(
                  'btn-mnu-attach-supporting-document'
                ),
              click: () => {
                this.attachDocument(credit);
              }
            }
          ].filter(a => a.visible)
        },
        {
          id: 'others',
          type: 'group',
          children: [
            {
              id: 'void',
              label: $localize`Void`,
              visible:
                credit.status &&
                ['VIG', 'VCD'].includes(credit.status.id) &&
                this.sessionService.isGranted('btn-mnu-void'),
              click: async () => {
                this.cancel(credit);
              }
            },
            {
              id: 'reactive',
              label: $localize`Reactivate`,
              visible:
                credit.status &&
                ['ANU'].includes(credit.status.id) &&
                this.sessionService.isGranted('btn-mnu-reactivate'),
              click: async () => {
                this.reactive(credit);
              }
            },
            {
              id: 'remove',
              label: $localize`Delete`,
              visible:
                credit.status &&
                ['ANU', 'CAN'].includes(credit.status.id) &&
                this.sessionService.isGranted('btn-mnu-delete'),
              click: async () => {
                this.remove(credit);
              }
            },
            {
              id: 'download',
              label: $localize`Download PDF`,
              visible:
                !this.appService.isMobile &&
                this.sessionService.isGranted('btn-mnu-download-pdf'),
              click: () => {
                this.share(credit, true);
              }
            },
            {
              id: 'share',
              label: $localize`Share PDF`,
              visible:
                Capacitor.isNativePlatform() &&
                this.sessionService.isGranted('btn-mnu-share-pdf'),
              click: () => {
                this.share(credit);
              }
            }
          ].filter(a => a.visible)
        }
      ].filter(g => g.children.length > 0);
    }
    return actions;
  }

  /**
   * @description Genera un documento formal para un crédito
   * @param {Credit} credit - Crédito para el cual generar el documento
   * @returns {Promise<void>} Promesa que resuelve cuando se genera el documento
   */
  async generateDocument(credit: Credit): Promise<void> {
    this.progressService.show({ modal: true });
    try {
      const mimeType: string = 'application/pdf';
      const lenderId = this.sessionService.settings()?.lenderId;
      const response = await this.apiService.call({
        microServiceName: 'WSMPCreditManagement',
        resourcePath: `/document/generate-formal-documents`,
        method: 'POST',
        useToken: true,
        body: {
          lenderId,
          operationNumber: credit.operationNumber
        },
        bodyEncrypted: true,
        responseEncrypted: true
      });
      const base64Document = response.data.base64Document;

      // Decodificar Base64 y crear un Blob
      const binary = atob(base64Document); // Decodifica Base64
      const binaryLength = binary.length;
      const bytes = new Uint8Array(binaryLength);
      for (let i = 0; i < binaryLength; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: mimeType }); // Cambia el tipo MIME según el contenido del documento.
      if (this.appService.isMobile) {
        this.nativeShare(base64Document, credit);
      } else {
        // Crear un enlace temporal y descargar el archivo
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = $localize`credit` + `-${credit.operationNumber ?? credit.operation?.number}.pdf`; // Cambia el nombre del archivo según tus necesidades.
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        // Liberar el objeto URL
        URL.revokeObjectURL(url);
      }
    } finally {
      this.progressService.hide();
    }
  }

  /**
   * @description Abre el diálogo de crédito
   * @param {Partial<Credit>} [credit] - Crédito a editar (opcional)
   * @returns {Promise<Operation>} Promesa con la operación resultante
   */
  open(credit?: Partial<Credit>, tab?: 'payments'): Promise<Operation> {
    return new Promise<Operation>(resolve => {
      const dialogRef = this.dialog.open(CreditComponent, {
        data: { credit, tab },
        panelClass: ['ft-dialog', 'ft-dialog--stacked'],
        height: '100vh',
        width: '682px',
        position: {
          left: 'auto',
          right: '0'
        }
      });
      dialogRef.componentInstance.afterSubmit.subscribe(
        (operation: Operation) => {
          this.change.emit(operation);
          resolve(operation);
        }
      );
    });
  }

  /**
   * @description Abre el diálogo de beneficiario
   * @param {Partial<Credit>} [credit] - Crédito asociado al beneficiario
   * @returns {Promise<Operation>} Promesa con la operación resultante
   */
  openBeneficiary(credit?: Partial<Credit>): Promise<Operation> {
    return new Promise<Operation>(resolve => {
      const dialogRef = this.dialog.open(BeneficiaryComponent, {
        data: { credit },
        panelClass: 'ft-dialog',
        width: '400px'
      });
      dialogRef.componentInstance.afterSubmit.subscribe(
        (operation: Operation) => {
          this.change.emit(operation);
          resolve(operation);
        }
      );
    });
  }

  /**
   * @description Reactiva un crédito cancelado
   * @param {Credit} credit - Crédito a reactivar
   * @returns {Promise<void>} Promesa que resuelve cuando se reactiva el crédito
   */
  async reactive(credit: Credit): Promise<void> {
    if (!(await this.allowNewCredit(credit))) return;

    const value = await firstValueFrom(
      this.messageService.show(
        $localize`Do you really want to reactivate the credit #${credit.operationNumber}?`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'warning',
            class: 'text-warning ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Cancel`,
              value: 0,
              type: 'stroked',
              class: 'flex-grow-1'
            },
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      )
    );
    if (value === 1) {
      await this.apiService.call({
        microServiceName: 'WSMPCreditManagement',
        resourcePath: `/credit/reactivate-credit`,
        method: 'POST',
        useToken: true,
        body: {
          countryId: credit.countryId,
          productTypeId: 'CON',
          operationNumber: credit.operationNumber
        },
        bodyEncrypted: true
      });
      this.messageService.show(
        $localize`Credit #${credit.operationNumber} has been successfully reactivated.`,
        { class: 'ft-message--success', icon: 'warning' }
      );
      this.change.emit({
        method: 'update',
        entity: credit
      });
    }
    return value;
  }

  /**
   * @description Verifica si el usuario puede crear/reactivar créditos según restricciones de suscripción
   * @param {Credit} credit - Crédito a validar
   * @returns {Promise<boolean>} true si está permitido, false si se muestra mensaje de restricción
   */
  async allowNewCredit(credit: Credit): Promise<boolean> {
    const currencyId = this.storageService.get(
      this.appService.currencyId,
      'local'
    );
    if (!this.appService.restrictions()?.maxCreditAmount) {
      const response: any = await this.apiService.call({
        microServiceName: 'WSMPApplicationSettings',
        resourcePath: `/appsrv/get-validation-rules/${currencyId}/CON/${this.sessionService.params()['profileId']}`,
        method: 'GET'
      });
      const country = response.data.find(
        (c: any) => c.countryId === credit.countryId
      );
      const maxCreditAmount = country?.validations.find(
        (r: any) => r.id === 'MPMMC'
      );
      if (maxCreditAmount) {
        this.appService.restrictions.update(r => {
          r.maxCreditAmount = Number(maxCreditAmount.value);
          return r;
        });
      }
    }
    const amountInvested =
      this.appService.restrictions()?.amountInvested?.[currencyId] ?? 0;
    const maxCreditAmount =
      this.appService.restrictions()?.maxCreditAmount ?? 0;
    const numOffersToActivate =
      this.appService.restrictions()?.numOffersToActivate ?? 0;

    if (numOffersToActivate <= 0) {
      this.messageService.show(
        $localize`You have reached the limit of active loans according to your current subscription.`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'warning',
            class: 'text-warning ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      );
      return false;
    } else if (amountInvested + credit.amount > maxCreditAmount) {
      this.messageService.show(
        $localize`You are exceeding the maximum credit amount you can register under your current subscription.`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'warning',
            class: 'text-warning ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      );
      return false;
    }
    return true;
  }

  /**
   * @description Elimina un crédito
   * @param {Credit} credit - Crédito a eliminar
   * @returns {Promise<void>} Promesa que resuelve cuando se elimina el crédito
   */
  async remove(credit: Credit): Promise<void> {
    const value = await firstValueFrom(
      this.messageService.show(
        $localize`Do you really want to delete credit #${credit.operationNumber}?`,
        {
          type: 'modal',
          class: 'text-center d-flex flex-column align-items-center gap-3',
          icon: {
            name: 'error',
            class: 'text-danger ft-icon--4',
            collection: 'factoricons-slim'
          },
          actions: [
            {
              label: $localize`Cancel`,
              value: 0,
              type: 'stroked',
              class: 'flex-grow-1'
            },
            {
              label: $localize`Accept`,
              value: 1,
              type: 'flat',
              class: 'flex-grow-1'
            }
          ]
        }
      )
    );
    if (value === 1) {
      await this.apiService.call({
        microServiceName: 'WSMPCreditManagement',
        resourcePath: `/credit/delete-credit/{operationNumber}/${credit.countryId}/COM`,
        method: 'DELETE',
        useToken: true,
        pathParams: {
          operationNumber: {
            value: credit.operationNumber,
            encrypted: true
          }
        }
      });
      this.change.emit({
        method: 'update',
        entity: credit
      });
      this.messageService.show($localize`Credit deleted successfully.`, {
        class: 'ft-message--success',
        icon: 'check--circle'
      });
    }
    return value;
  }

  /**
   * @description Comparte un crédito
   * @param {Credit} credit - Crédito a compartir
   * @returns {Promise<Operation>} Promesa con la operación resultante
   */
  async share(credit: Credit, download?: boolean): Promise<Operation> {
    return new Promise<Operation>(resolve => {
      const dialogRef = this.dialog.open(SharingOptionsComponent, {
        data: { credit, download },
        panelClass: ['ft-dialog'],
        width: '400px'
      });
      dialogRef.componentInstance.afterSubmit.subscribe(
        (operation: Operation) => {
          this.change.emit(operation);
          resolve(operation);
        }
      );
    });
  }

  /**
   * @description Comparte un crédito
   * @param {Credit} credit - Crédito a compartir
   * @returns {Promise<void>} Promesa
   */
  async nativeShare(data: string, credit: Credit): Promise<void> {
    const path = $localize`credit` + `-${credit.operationNumber ?? credit.operation?.number}.pdf`;
    await Filesystem.writeFile({
      path,
      data,
      directory: Directory.Cache
    });
    const uriResult = await Filesystem.getUri({
      directory: Directory.Cache,
      path
    });
    Share.share({
      files: [uriResult.uri],
      title: ''
    });
  }
}
